import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Tooltip, keyframes } from '@mui/material';
import axios from 'axios';
import { eventBus, EVENTS } from '../utils/eventBus';
import { apiClient } from '../../api/client';
// import { useDataSocket } from '../../context/DataSocketContext'; // Removed unused import
import { debounce } from 'lodash';
import { getSocket } from '../../utils/socketManager';
import { io } from 'socket.io-client';

// Define API base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || window.location.origin;

// Helper function to check if a date is in the future
const isFutureDate = (dateStr) => {
  if (!dateStr) return false;
  try {
    const date = new Date(dateStr);
    return date > new Date();
  } catch (e) {
    return false;
  }
};

// Define keyframes for the pulsating animation
const pulse = keyframes`
  0% {
    opacity: 0.9;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
    box-shadow: 0 0 6px 3px rgba(255, 255, 255, 0.2);
  }
  100% {
    opacity: 0.9;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
`;

// More intense pulsating for active generation
const activePulse = keyframes`
  0% {
    opacity: 0.95;
    transform: scale(1);
    box-shadow: 0 0 3px 1px rgba(255, 255, 255, 0.4);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
    box-shadow: 0 0 8px 4px rgba(255, 255, 255, 0.3);
  }
  100% {
    opacity: 0.95;
    transform: scale(1);
    box-shadow: 0 0 3px 1px rgba(255, 255, 255, 0.4);
  }
`;

// Add a strong flash animation for maintenance
const maintenanceFlash = keyframes`
  0% { opacity: 1; background: #fffde7; box-shadow: 0 0 8px 2px #ffeb3b; }
  50% { opacity: 0.5; background: #fffde7; box-shadow: 0 0 24px 8px #ffeb3b; }
  100% { opacity: 1; background: #fffde7; box-shadow: 0 0 8px 2px #ffeb3b; }
`;

export const SchedulerStatusIndicator = ({ style = 'rounded' }) => {
  const [status, setStatus] = useState({
    isRunning: false,
    isEnabled: false,
    batchInfo: null,
    currentTask: null,
    loading: true,
    error: null,
    source: null, // Add source field to track manual vs scheduled runs
    hasEnabledScheduler: false,
    socketConnected: false,
    socketError: null
  });

  const socketRef = useRef(null);
  // Removed fetchStatusRef and batchConfigTimerRef since we're not using polling anymore
  // WebSocket events provide instant updates for scheduler status and batch configs
  const lastFetchTimeRef = useRef(0);
  const reconnectAttemptsRef = useRef(0);
  const didFallbackToRestRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 3;
  
  // Refs to store previous task details for comparison
  const prevTaskRef = useRef(null);
  const prevIsRunningRef = useRef(false);
  
  const [maintenanceWarning, setMaintenanceWarning] = useState({ active: false, message: '' });
  
  // Function to fetch batch configurations (not status, which will come via WebSocket)
  const fetchBatchConfigs = useCallback(async (force = false) => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;
    
    // Rate limiting - if not forced and we fetched recently (within 10 seconds), skip this fetch
    if (!force && timeSinceLastFetch < 10000) {
      return;
    }
    
    // Update last fetch time
    lastFetchTimeRef.current = now;
    
    try {
      // Check if any batches are enabled or counting down
      const configsResponse = await apiClient.get('/saved-batch-configs');
      
      if (configsResponse.data && configsResponse.data.success) {
        const configs = configsResponse.data.data || [];
        
        // Check for enabled batches or batches with a future next_run_at
        const enabledBatches = configs.filter(config => 
          config.schedule_enabled || isFutureDate(config.next_run_at)
        );
        
        // Get the batch with the closest next run time
        let nextBatchInfo = null;
        let closestTime = null;
        
        enabledBatches.forEach(batch => {
          if (batch.next_run_at) {
            const nextRunTime = new Date(batch.next_run_at);
            if (!closestTime || nextRunTime < closestTime) {
              closestTime = nextRunTime;
              nextBatchInfo = batch;
            }
          }
        });
        
        // Calculate if we should show countdown (only if next batch is due within 10 seconds)
        const shouldShowCountdown = nextBatchInfo && closestTime && 
          ((new Date(closestTime) - new Date()) / 1000 <= 10);
        
        setStatus(prev => ({
          ...prev,
          isEnabled: enabledBatches.length > 0,
          isCountingDown: shouldShowCountdown,
          nextBatchInfo,
          nextRunTime: closestTime,
          loading: false,
          error: null
        }));
        
      } else {
        throw new Error('Invalid response format from saved-batch-configs');
      }
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to fetch batch configurations'
      }));
    }
  }, []);
  
  // Function to fetch initial scheduler status (will be used only once at startup)
  const fetchInitialStatus = useCallback(async () => {
    try {
      // Fetching initial scheduler status via REST API
      // Use apiClient instead of direct fetch to ensure consistent auth handling and base URL
      const statusResponse = await apiClient.get('/scheduler/status');
      
      if (statusResponse.data && statusResponse.data.data) {
        const responseData = statusResponse.data.data;
        // Ensure we have the appropriate fields
        const isRunning = responseData.isRunning || false;
        const batchInfo = responseData.batchInfo || null;
        const currentTask = responseData.currentTask || null;
        const progress = responseData.progress || 0;
        
        // Initial batch status received
        
        setStatus(prev => ({
          ...prev,
          isRunning,
          batchInfo,
          currentTask,
          progress,
          loading: false, // Explicitly set loading to false
          error: null,
          socketConnected: false // Reset socket status on API fallback
        }));

        didFallbackToRestRef.current = true;
        // Initialize prev refs
        prevTaskRef.current = currentTask;
        prevIsRunningRef.current = isRunning;
      } else {
        // If we received a response but it doesn't have the expected data structure
        console.warn('Received response with unexpected structure:', statusResponse.data);
        setStatus(prev => ({
          ...prev,
          loading: false,
          error: null
        }));

        didFallbackToRestRef.current = true;
      }
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        loading: false, // Also set loading to false on error
        socketError: error.message
      }));

      didFallbackToRestRef.current = true;
    }
  }, []);

  // Handle scheduler status updates
  const handleSchedulerStatus = useCallback((data) => {
    // Received scheduler status update
    if (!data) return;

    // Handle both direct scheduler_status events and data_update wrapped events
    const statusData = data.type === 'scheduler_status' ? data.data : data;

    const newIsRunning = statusData.isRunning ?? false;
    const newCurrentTask = statusData.currentTask || null;

    // Check if task completed or batch finished
    // Task completed if newCurrentTask is different from prevTaskRef.current (and prevTaskRef.current was not null)
    // or if the batch was running and now it's not.
    const taskJustCompleted = prevTaskRef.current && 
                             (JSON.stringify(newCurrentTask) !== JSON.stringify(prevTaskRef.current));
    const batchJustFinished = prevIsRunningRef.current && !newIsRunning;

    if ((taskJustCompleted || batchJustFinished) && prevTaskRef.current) {
      const completedTask = prevTaskRef.current; // Use the task that was just active
      const viewId = completedTask.view_id || completedTask.view; // Prefer view_id if available
      const pillarId = completedTask.pillar_id || completedTask.pillar; // Prefer pillar_id
      let dataType = completedTask.type;

      // Basic mapping of backend task types to frontend data types
      // This might need to be more sophisticated based on actual backend types
      if (typeof dataType === 'string') {
        if (dataType.toLowerCase().includes('kpi') || dataType.toLowerCase().includes('indicator')) {
          dataType = 'indicators';
        } else if (dataType.toLowerCase().includes('event')) {
          dataType = 'events';
        } else if (dataType.toLowerCase().includes('analysis') && !dataType.toLowerCase().includes('map_analysis')) {
          dataType = 'analysis';
        } else if (dataType.toLowerCase().includes('acled')) {
          dataType = 'acled';
        } else if (dataType.toLowerCase().includes('dtm')) {
          dataType = 'dtm';
        } else if (dataType.toLowerCase().includes('map_analysis')) {
          dataType = 'map_analysis';
        }
        // Add more mappings as needed
      }

      if (viewId && dataType) { // pillarId can be null for view-wide data
        // Emitting BATCH_TASK_COMPLETED event
        eventBus.publish(EVENTS.BATCH_TASK_COMPLETED, {
          viewId,
          pillarId,
          dataType,
          taskDetails: completedTask // Pass along original task details if needed later
        });
      }
    }
    
    // Update current status (skip state update if nothing meaningful changed)
    setStatus(prev => {
      const next = {
        ...prev,
        isRunning: newIsRunning,
        batchInfo: statusData.batchInfo || null,
        currentTask: newCurrentTask,
        progress: statusData.progress || 0,
        source: statusData.source || null,
        loading: false,
        error: null,
        socketConnected: true,
        socketError: null
      };

      const sameIsRunning = prev.isRunning === next.isRunning;
      const sameProgress = prev.progress === next.progress;
      const sameSource = prev.source === next.source;
      const sameTask = JSON.stringify(prev.currentTask) === JSON.stringify(next.currentTask);
      const sameBatchInfo = JSON.stringify(prev.batchInfo) === JSON.stringify(next.batchInfo);

      if (sameIsRunning && sameProgress && sameSource && sameTask && sameBatchInfo && prev.socketConnected === true && prev.socketError === null) {
        return prev;
      }
      return next;
    });

    // Update refs for next comparison
    prevTaskRef.current = newCurrentTask;
    prevIsRunningRef.current = newIsRunning;

  }, []);

  // Handle socket connection event
  const handleConnect = useCallback(() => {
          // Socket connected
    reconnectAttemptsRef.current = 0;
    
    setStatus(prev => ({
      ...prev,
      socketConnected: true,
      socketError: null
    }));
    
    // Request current status when connected
    if (socketRef.current) {
      // Requesting status via request_data
      socketRef.current.emit('request_data', {});
      // Also emit request_status for backward compatibility
      socketRef.current.emit('request_status');
    }
  }, []);

  // Handle socket connection error
  const handleConnectError = useCallback((error) => {
          // WebSocket connection error handled
    reconnectAttemptsRef.current++;
    
    setStatus(prev => ({
      ...prev,
      socketConnected: false,
      socketError: `Connection error: ${error.message || error}`
    }));

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
              // Max reconnection attempts reached, falling back to REST API
      if (!didFallbackToRestRef.current) {
        fetchInitialStatus();
      }
    }
  }, [fetchInitialStatus]);

  // Setup WebSocket connection and event handlers
  useEffect(() => {
    // Setting up WebSocket connection for batch updates
    
    // Fetch initial status immediately, don't wait for socket to connect
    fetchInitialStatus();
    fetchBatchConfigs(true);
    
    // Disconnect any existing socket before creating a new one
    if (socketRef.current) {
      try {
        socketRef.current.off('connect');
        socketRef.current.off('scheduler_status');
        socketRef.current.off('connect_error');
        socketRef.current.off('disconnect');
        socketRef.current.off('data_update');
        socketRef.current.off('maintenance_warning');
        socketRef.current.disconnect();
      } catch (_) {}
      socketRef.current = null;
    }

    // Debounce connection to prevent rapid churn
    const timeoutId = setTimeout(() => {
      // Create a direct socket connection to the batch-updates namespace
      if (!socketRef.current) {
        const origin = window.location.origin;
        const connectUrl = import.meta.env.DEV
          ? `${window.location.protocol}//${window.location.hostname}:5001`
          : (origin.endsWith('/') ? origin.slice(0, -1) : origin);
        
        // Connecting to batch-updates namespace
        
        // Always allow WebSocket upgrades, even in production
        const socket = io(`${connectUrl}/batch-updates`, {
          path: '/socket.io',
          transports: ['websocket', 'polling'],
          upgrade: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
          autoConnect: true,
          withCredentials: true,
          auth: {
            token: localStorage.getItem('token')
          }
        });
        
        socketRef.current = socket;
        
        // Set up event listeners
        socketRef.current.on('connect', handleConnect);
        
        socketRef.current.on('scheduler_status', (data) => {
          // Direct scheduler_status event received
          handleSchedulerStatus(data);
        });
        
        socketRef.current.on('data_update', (data) => {
          // Only log non-batch updates to reduce noise
          if (data && data.type !== 'batch') {
            // data_update event received
          }
          if (data && data.type === 'scheduler_status') {
            handleSchedulerStatus(data);
          }
          // Handle batch configuration updates instantly via websocket
          if (data && data.type === 'configs_update') {
            // Configs updated via websocket, refreshing
            fetchBatchConfigs(true);
          }
        });
        
        socketRef.current.on('connect_error', handleConnectError);
        
        socketRef.current.on('disconnect', (reason) => {
          // Socket disconnected
          setStatus(prev => ({
            ...prev,
            socketConnected: false,
            socketError: `Disconnected: ${reason}`
          }));
        });

        // Listen for maintenance_warning event
        socketRef.current.on('maintenance_warning', (payload) => {
          setMaintenanceWarning({ active: true, message: payload?.message || 'The site will go down for maintenance soon!' });
        });

        // Listen for maintenance_clear event
        socketRef.current.on('maintenance_clear', () => {
          setMaintenanceWarning({ active: false, message: '' });
        });
      }
    }, 500); // 500ms debounce

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      if (socketRef.current) {
        // Cleaning up socket
        try {
          socketRef.current.off('connect');
          socketRef.current.off('scheduler_status');
          socketRef.current.off('connect_error');
          socketRef.current.off('disconnect');
          socketRef.current.off('data_update');
          socketRef.current.off('maintenance_warning');
          socketRef.current.off('maintenance_clear');
          socketRef.current.disconnect();
        } catch (_) {}
        socketRef.current = null;
      }
    };
  }, [fetchInitialStatus, fetchBatchConfigs, handleSchedulerStatus, handleConnect, handleConnectError]);
  
  const getIndicatorProps = () => {
    if (maintenanceWarning.active) {
      return {
        color: '#ffeb3b',
        message: maintenanceWarning.message,
        animation: `${maintenanceFlash} 0.8s infinite`,
        visible: true
      };
    }
    // Don't show any status while loading, UNLESS we already know a batch is running
    if (status.loading && !status.isRunning) {
      return {
        color: 'transparent', // Make it invisible while loading
        message: 'Loading scheduler status...',
        animation: 'none',
        visible: false
      };
    }
    
    // If we're loading but we already know a batch is running, show the running indicator
    if (status.loading && status.isRunning) {
      return {
        color: '#2196f3', // Blue for running
        message: 'Running: Batch generation in progress',
        animation: `${activePulse} 1.5s infinite`,
        visible: true
      };
    }
    
    if (status.error) {
      if (status.authError) {
        return {
          color: '#ff5555',
          message: 'Authentication required to check scheduler status',
          animation: 'none',
          visible: true
        };
      }
      return {
        color: '#ff5555',
        message: 'Error: Could not fetch scheduler status',
        animation: 'none',
        visible: true
      };
    }
    
    if (status.isRunning) {
      // Get the current task information
      const currentTask = status.currentTask;
      const totalTasks = status.batchInfo?.totalTasks || 0;
      const completedTasks = status.batchInfo?.completedTasks || 0;
      const remainingTasks = totalTasks - completedTasks;
      
      // Build the detailed message
      let message = '';
      if (status.source === 'run_now') {
        message = 'Manual Run: ';
      } else {
        message = 'Running: ';
      }
      
      // Add batch name if available
      if (status.batchInfo?.name) {
        message += `${status.batchInfo.name}\n`;
      }
      
      // Add current task details if available
      if (currentTask) {
        const viewName = currentTask.view || 'Unknown View';
        const pillarName = currentTask.pillar || 'N/A';
        const taskType = currentTask.type || 'Unknown Type';
        
        // Format the task type to be more readable
        const formattedTaskType = taskType
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        message += `\nCurrent Task:\n`;
        message += `• View: ${viewName}\n`;
        if (pillarName !== 'N/A') {
          message += `• Pillar: ${pillarName}\n`;
        }
        message += `• Type: ${formattedTaskType}\n`;
      }
      
      // Add progress information
      if (totalTasks > 0) {
        message += `\nProgress: ${completedTasks}/${totalTasks} tasks completed`;
        if (remainingTasks > 0) {
          message += ` (${remainingTasks} remaining)`;
        }
      }
      
      return {
        color: '#2196f3', // Blue for running
        message,
        animation: `${activePulse} 1.5s infinite`,
        visible: true
      };
    }
    
    if (status.isCountingDown && status.nextRunTime) {
      const timeRemaining = Math.max(0, Math.floor((new Date(status.nextRunTime) - new Date()) / 1000));
      const minutes = Math.floor(timeRemaining / 60);
      const seconds = timeRemaining % 60;
      const timeDisplay = timeRemaining <= 10 ? `${seconds}s` : `${minutes}m ${seconds}s`;
      
      return {
        color: '#ff9800',
        message: `Countdown: Next batch runs in ${timeDisplay} (${status.nextBatchInfo?.name || 'Scheduled batch'})`,
        animation: `${pulse} 2s infinite`,
        visible: true
      };
    }
    
    if (status.isEnabled) {
      return {
        color: '#4caf50',
        message: 'Active: Scheduler is monitoring and will automatically execute batches according to their configured schedules',
        animation: `${pulse} 3s infinite`,
        visible: true
      };
    }
    
    return {
      color: '#aaaaaa',
      message: 'Ready: Scheduler is available and waiting for batch configurations',
      animation: 'none',
      visible: true
    };
  };
  
  const { color, message, animation, visible } = getIndicatorProps();
  
  // Determine status text
  const statusText = status.isRunning ? 'GENERATING' : 
                     status.isCountingDown ? 'INITIALIZING' : 
                     status.isEnabled ? 'ACTIVE' : 'READY';

  // Get indicator styles based on style prop
  const getIndicatorStyles = () => {
    if (style === 'flat') {
      return {
        container: {
          width: 12,
          height: 12,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        indicator: {
          width: '100%',
          height: '100%',
          borderRadius: '2px',
          backgroundColor: color,
          animation,
          border: '1px solid rgba(0,0,0,0.1)',
          boxShadow: `0 1px 3px rgba(0,0,0,0.2)`,
        }
      };
    } else {
      // Default 'rounded' (LED) style
      return {
        container: {
          width: 14,
          height: 14,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        outerRing: {
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #666 0%, #444 100%)',
          border: '1px solid rgba(0,0,0,0.15)',
          boxShadow: `
            inset 0 0 1px rgba(255,255,255,0.2),
            0 0 1px rgba(0,0,0,0.2)
          `,
        },
        indicator: {
          width: '75%',
          height: '75%',
          borderRadius: '50%',
          position: 'relative',
          backgroundColor: color,
          opacity: 0.95,
          animation,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '50%',
            background: `radial-gradient(
              circle at 30% 30%,
              rgba(255,255,255,0.4) 0%,
              rgba(255,255,255,0.2) 30%,
              rgba(255,255,255,0) 70%
            )`,
            opacity: 0.9,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '15%',
            left: '30%',
            width: '20%',
            height: '20%',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.8)',
            filter: 'blur(0.5px)',
          },
          boxShadow: `
            0 0 3px ${color}40,
            0 0 5px ${color}20,
            inset 0 0 2px rgba(255,255,255,0.3),
            inset 0 -2px 2px rgba(0,0,0,0.2)
          `,
        }
      };
    }
  };

  const indicatorStyles = getIndicatorStyles();
  
  // Don't render anything until we have a final status
  if (!visible) {
    return null;
  }
  
  return (
    <Tooltip 
      title={
        <Box sx={{ 
          width: '280px',
          p: 0.75,
          '& .tooltip-section': {
            mb: 0.5,
            '&:last-child': {
              mb: 0
            }
          },
          '& .tooltip-title': {
            fontWeight: 600,
            color: '#000',
            mb: 0.25,
            fontSize: 'var(--font-size-md)'
          },
          '& .tooltip-content': {
            color: 'rgba(0, 0, 0, 0.9)',
            fontSize: 'var(--font-size-md)',
            lineHeight: 1.3
          },
          '& .tooltip-progress': {
            mt: 1,
            pt: 1,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            color: '#2196f3',
            fontWeight: 500
          },
          '& .tooltip-task': {
            mt: 1,
            pt: 1,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)'
          },
          '& .task-detail': {
            display: 'flex',
            alignItems: 'center',
            mb: 0.5,
            '&:last-child': {
              mb: 0
            }
          },
          '& .task-detail-icon': {
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
            flexShrink: 0
          },
          '& .task-detail-value': {
            color: 'rgba(0, 0, 0, 0.9)',
            fontSize: 'var(--font-size-md)'
          }
        }}>
          {typeof message === 'string' ? (
            message.split('\n').map((line, index) => {
              if (line.startsWith('Active:') || line.startsWith('Ready:') || line.startsWith('Running:') || line.startsWith('Manual Run:')) {
                return (
                  <Box key={index} className="tooltip-section">
                    <Typography className="tooltip-title">
                      {line.startsWith('Active:') ? 'Active' : 
                       line.startsWith('Ready:') ? 'Ready' : 
                       line.startsWith('Running:') ? 'Running' : 'Manual Run'}
                    </Typography>
                    <Typography className="tooltip-content">
                      {line.replace(/^(Active|Ready|Running|Manual Run):/, '').trim()}
                    </Typography>
                  </Box>
                );
              } else if (line.startsWith('Current Task:')) {
                return (
                  <Box key={index} className="tooltip-section tooltip-task">
                    <Typography className="tooltip-title">Current Task</Typography>
                  </Box>
                );
              } else if (line.startsWith('Progress:')) {
                return (
                  <Box key={index} className="tooltip-section tooltip-progress">
                    <Typography className="tooltip-content">{line}</Typography>
                  </Box>
                );
              } else if (line.startsWith('•')) {
                // Extract the value from the bullet point
                const content = line.replace('•', '').trim();
                const value = content.split(':').pop().trim();
                
                return (
                  <Box key={index} className="tooltip-section">
                    <Box className="task-detail">
                      <Box className="task-detail-icon">
                        <Box sx={{ 
                          width: 6, 
                          height: 6, 
                          borderRadius: '50%', 
                          backgroundColor: '#2196f3' 
                        }} />
                      </Box>
                      <Typography className="task-detail-value">{value}</Typography>
                    </Box>
                  </Box>
                );
              }
              return (
                <Box key={index} className="tooltip-section">
                  <Typography className="tooltip-content">{line}</Typography>
                </Box>
              );
            })
          ) : message}
        </Box>
      } 
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(255, 255, 255, 0.95) !important',
            '& .MuiTooltip-arrow': {
              color: 'rgba(255, 255, 255, 0.95) !important'
            },
            '& .MuiTooltip-tooltip': {
              bgcolor: 'rgba(255, 255, 255, 0.95) !important',
              color: '#000 !important',
              padding: '0.75rem !important',
              maxWidth: '280px !important',
              width: '280px !important',
              lineHeight: '1.3 !important',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15) !important',
              border: '1px solid rgba(0, 0, 0, 0.08) !important'
            }
          }
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        cursor: 'default',
      }}>
        {/* Dynamic status indicator */}
        <Box sx={indicatorStyles.container}>
          {style === 'rounded' && indicatorStyles.outerRing && (
            <Box sx={indicatorStyles.outerRing} />
          )}
          <Box sx={indicatorStyles.indicator} />
        </Box>
        
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: 'var(--font-size-md)',
            opacity: 0.9,
            display: { xs: 'none', sm: 'block' },
            fontWeight: 500,
            letterSpacing: '0.5px'
          }}
        >
          {statusText}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default React.memo(SchedulerStatusIndicator);
